import {
  Card,
  Grid2,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  AlertTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from 'yup';
import api from '../../../redux/features/auth/axiosAuthHelper';
import {
  CustomFieldTypeSelectField,
  TextEditField,
  TextEditReadOnlyField,
} from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import RareButton from '../buttons/RareButton';
import { CustomField } from '../../../components/common/types';
import Fieldset from '../fields/Fieldset';

interface CustomFieldCardProps {
  customFieldId?: any | null;
  onBack: () => void;
  onSave: () => void;
}

const CustomFieldCard: React.FC<CustomFieldCardProps> = ({
  customFieldId,
  onBack,
  onSave,
}) => {
  const authData = useSelector((state: RootState) => state.auth);
  const [errors, setErrors] = useState<string | null>(null);
  const create = !customFieldId;

  const validationSchema = () => {
    return Yup.object().shape({
      type: Yup.string().required('This field is required'),
    });
  };

  const formik = useFormik({
    enableReinitialize: !create,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      type: '',
      key: '',
      title: '',
      placeholder: '',
      required: false,
      required_error: '',
      validation_error: '',
      regex: '',
      range1: null,
      range2: null,
      options: [],
      default_index: '',
      newOption: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const url = create
        ? 'dynamic-fields/'
        : `dynamic-fields/${customFieldId}/`;
      const method = create ? 'post' : 'patch';
      const extraData: any = {};
      if (values.type === 'NUMBER_ENTRY') {
        if (values.range1 != null && values.range2 != null) {
          extraData['range'] = [values.range1, values.range2];
        }
      } else if (values.type === 'TEXT_ENTRY') {
        extraData['regex'] = values.regex;
      } else if (values.type === 'TEXT_PICKER') {
        extraData['options'] = values.options;
        extraData['default_index'] = values.default_index;
      } else if (values.type === 'BARCODE') {
        const date = Date.now();
        extraData['regex'] = values.regex;
        extraData['key'] = date;
        extraData['title'] = date;
        extraData['placeholder'] = '';
        extraData['required'] = true;
        extraData['required_error'] = '';
        extraData['validation_error'] = '';
      }
      const data = {
        type: values.type,
        key: values.key,
        title: values.title,
        placeholder: values.placeholder,
        required: values.required,
        required_error: values.required_error,
        validation_error: values.validation_error,
        ...extraData,
      };
      console.log(data);
      try {
        const result = await api({
          method,
          url,
          data,
          headers: {
            Authorization: `Bearer ${authData.access}`,
          },
        });
        setErrors(null);
        console.log(
          `Custom field successfully ${create ? 'created' : 'updated'}.`
        );
        onSave();
      } catch (error: any) {
        if (error?.response?.status === 400) {
          const firstErrorItem = Object.keys(error.response.data)[0];
          setErrors(
            `${firstErrorItem}: ${error.response.data[firstErrorItem]}`
          );
        } else {
          const errorText = `Unknown error ${create ? 'creating' : 'updating'
            } custom field`;
          setErrors(errorText);
          console.error(errorText, error);
        }
      }
    },
  });

  useEffect(() => {
    if (customFieldId) {
      const fetch = async () => {
        try {
          const url = `dynamic-fields/${customFieldId}`;

          const response = await api.get(url, {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          const data = response.data as CustomField;

          formik.setFieldValue('id', data.id);
          formik.setFieldValue('organisation', data.organisation);
          formik.setFieldValue('type', data.type);
          formik.setFieldValue('key', data.key);
          formik.setFieldValue('title', data.title);
          formik.setFieldValue('placeholder', data.placeholder);
          formik.setFieldValue('required', data.required);
          formik.setFieldValue('required_error', data.required_error);
          formik.setFieldValue('validation_error', data.validation_error);
          formik.setFieldValue('regex', data.regex);
          if (data.range?.length === 2) {
            formik.setFieldValue('range1', data.range[0]);
            formik.setFieldValue('range2', data.range[1]);
          }
          formik.setFieldValue('options', data.options);
          formik.setFieldValue('default_index', data.default_index);
        } catch (error) {
          console.error('Error fetching custom field', error);
          setErrors(`No custom field found with ID ${customFieldId}`);
          setTimeout(() => {
            onBack();
          }, 4000);
        }
      };
      fetch();
    }
  }, [customFieldId]);

  const handleAddOption = () => {
    if (formik.values.newOption.trim() !== '') {
      console.log('EEEE', formik.values.newOption.trim());
      formik.setFieldValue('options', [
        ...formik.values.options,
        formik.values.newOption.trim(),
      ]);
      formik.setFieldValue('newOption', '');
    }
  };

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} style={{ overflow: 'auto' }}>
        <Card
          sx={{
            height: '100%',
            boxShadow: 10,
            p: 2,
            marginX: 30,
            marginY: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              margin: 'auto',
              padding: '1em',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Grid2
              container
              spacing={5}
              alignItems="flex-start"
              justifyContent="center"
              style={{ minWidth: '100%', padding: 5 }}
            >
              <Grid2
                sx={{
                  width: { xs: '100%', md: '45%' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Card
                  sx={{
                    pb: 1,
                    boxShadow: 10,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Box
                      sx={{
                        height: '100%',
                        background: colorConfigs.tables.headBg,
                        display: 'flex',
                        alignItems: 'center',
                        borderBottomRightRadius: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          px: 2,
                          py: 0.5,
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '20px',
                        }}
                      >
                        Custom Field Details
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      pt: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    {!create && (
                      <Field
                        name="id"
                        disabled
                        type="text"
                        className="form-control"
                        label="Id"
                        component={TextEditReadOnlyField}
                      />
                    )}
                    <Field
                      name="type"
                      type="text"
                      className="form-control"
                      label="Type"
                      component={CustomFieldTypeSelectField}
                    />
                    {formik.values.type !== 'BARCODE' && (
                      <>
                        <Field
                          name="key"
                          type="text"
                          className="form-control"
                          label="Key"
                          component={TextEditField}
                        />
                        <Field
                          name="title"
                          type="text"
                          className="form-control"
                          label="Title"
                          component={TextEditField}
                        />
                        <Field
                          name="placeholder"
                          type="text"
                          className="form-control"
                          label="Placeholder"
                          component={TextEditField}
                        />
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Checkbox
                              checked={formik.values.required}
                              name="required"
                              sx={{
                                '&.Mui-checked': {
                                  color: colorConfigs.buttons.active,
                                },
                              }}
                              onChange={formik.handleChange}
                              className="form-control"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center">
                              Required
                            </Box>
                          }
                        />
                      </>
                    )}
                  </Box>
                </Card>
                {errors && (
                  <Alert
                    severity="error"
                    style={{ width: '100%', marginTop: '15px' }}
                  >
                    <AlertTitle>Error</AlertTitle>
                    {errors}
                  </Alert>
                )}
              </Grid2>

              <Grid2
                sx={{
                  width: { xs: '100%', md: '45%' },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Card
                  sx={{
                    pb: 1,
                    boxShadow: 10,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Box
                      sx={{
                        height: '100%',
                        background: colorConfigs.tables.headBg,
                        display: 'flex',
                        alignItems: 'center',
                        borderBottomRightRadius: 3,
                        // visibility: 'hidden',
                      }}
                    >
                      <Typography
                        sx={{
                          px: 2,
                          py: 0.5,
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '20px',
                        }}
                      >
                        Additional fields
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      pt: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    {formik.values.type !== 'BARCODE' && (
                      <Field
                        name="required_error"
                        type="text"
                        className="form-control"
                        label="Required Error"
                        component={TextEditField}
                      />
                    )}
                    <Field
                      name="validation_error"
                      type="text"
                      className="form-control"
                      label="Validation Error"
                      component={TextEditField}
                    />
                    {(formik.values.type === 'TEXT_ENTRY' ||
                      formik.values.type === 'BARCODE') && (
                        <Field
                          name="regex"
                          type="text"
                          className="form-control"
                          label="Regex"
                          component={TextEditField}
                        />
                      )}
                    {formik.values.type === 'NUMBER_ENTRY' && (
                      <>
                        <Field
                          name="range1"
                          type="number"
                          className="form-control"
                          label="Range lower limit"
                          component={TextEditField}
                        />
                        <Field
                          name="range2"
                          type="number"
                          className="form-control"
                          label="Range upper limit"
                          component={TextEditField}
                        />
                      </>
                    )}
                    {formik.values.type === 'TEXT_PICKER' && (
                      <>
                        <Fieldset title="Options">
                          <Box
                            sx={{
                              paddingY: 0,
                              paddingX: 0,
                              marginTop: 1,
                              minHeight: '150px',
                              maxHeight: '150px',
                              overflow: 'auto', // Enable scrolling when content exceeds the height
                              backgroundColor: '#F0F0F0',
                              border: '1px solid rgba(0, 0, 0, 0.5)',
                              borderRadius: 2,
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            <List
                              dense
                              sx={{
                                paddingX: 0,
                                marginX: 0,
                              }}
                            >
                              {formik.values.options.map((option, index) => (
                                <ListItem
                                  key={index}
                                  sx={{
                                    paddingY: 0,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover .delete-button': { opacity: 1 },
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography sx={{ color: '#000' }}>
                                        {option}
                                      </Typography>
                                    }
                                  />
                                  <IconButton
                                    className="delete-button"
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.3s',
                                    }}
                                    onClick={() => {
                                      const newOptions = [
                                        ...formik.values.options,
                                      ];
                                      newOptions.splice(index, 1);
                                      formik.setFieldValue(
                                        'options',
                                        newOptions
                                      );
                                    }}
                                    size="small"
                                    color="error"
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, paddingTop: 3 }}>
                            <Field
                              name="newOption"
                              type="text"
                              className="form-control"
                              label="New Option"
                              component={TextEditField}
                            />
                            <IconButton
                              onClick={handleAddOption}
                              color="primary"
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Fieldset>
                        <Field
                          name="default_index"
                          type="text"
                          className="form-control"
                          label="Default Index"
                          component={TextEditField}
                        />
                      </>
                    )}
                  </Box>
                </Card>
              </Grid2>
            </Grid2>
          </div>
          <div
            className="form-group"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <RareButton
              onClick={onBack}
              variant="outlined"
              sx={{
                position: 'relative',
                m: 1,
              }}
            >
              Back
            </RareButton>
            <RareButton
              type="submit"
              variant="success"
              sx={{
                position: 'relative',
                m: 1,
              }}
            >
              Save Changes
            </RareButton>
          </div>
        </Card>
      </form>
    </FormikProvider>
  );
};

export default CustomFieldCard;
